import { RSP_CONTRACT_ABI, RSP_CONTRACT_ADDRESS } from '../constants/contract';
import type { useSendTransaction } from '@privy-io/react-auth';
import { utils } from 'ethers';
import { PublicClient, WalletClient, encodeFunctionData } from 'viem';

import { MovesEnum } from 'shared/constants/shared-enums';

const contract = RSP_CONTRACT_ADDRESS as `0x${string}`;

export type SendTransactionFn = ReturnType<typeof useSendTransaction>['sendTransaction'];

export interface IMakeMove {
  roomId: bigint;
  roundCounter: number;
  salt: string;
  move: MovesEnum;
  walletClient: WalletClient;
  publicClient: PublicClient;
  userAddress: `0x${string}`;
  isPrivyWallet: boolean;
  sendTransaction: SendTransactionFn;
}

export async function makeMove({
  roomId,
  roundCounter,
  salt,
  move,
  walletClient,
  publicClient,
  userAddress,
  isPrivyWallet,
  sendTransaction,
}: IMakeMove) {
  const moveHash = utils.solidityKeccak256(
    ['string', 'uint8', 'address'],
    [salt, move, userAddress],
  ) as `0x${string}`;

  if (isPrivyWallet) {
    const data = encodeFunctionData({
      abi: RSP_CONTRACT_ABI,
      functionName: 'playRoomGame',
      args: [roomId, roundCounter, moveHash],
    });
    let hash = await sendTransaction(
      {
        to: contract,
        data: data,
      },
      {
        //TODO: remove before production
        // sponsor: true, // ✅ Privy Paymaster covers gas fees
        uiOptions: {
          showWalletUIs: false,
        },
      },
    );
    return hash?.hash ?? null;
  }
  const { request } = await publicClient.simulateContract({
    address: contract,
    abi: RSP_CONTRACT_ABI,
    functionName: 'playRoomGame',
    args: [roomId, roundCounter, moveHash],
    account: userAddress,
  });
  let response = await walletClient.writeContract({ ...request });
  return response;
}

export interface IRevealMove {
  roomId: bigint;
  roundCounter: number;
  move: MovesEnum;
  salt: string;
  walletClient: WalletClient;
  publicClient: PublicClient;
  userAddress: `0x${string}`;
  isPrivyWallet: boolean;
  sendTransaction: SendTransactionFn;
}

export async function revealMove({
  roomId,
  roundCounter,
  move,
  salt,
  walletClient,
  publicClient,
  userAddress,
  isPrivyWallet,
  sendTransaction,
}: IRevealMove) {
  if (isPrivyWallet) {
    const data = encodeFunctionData({
      abi: RSP_CONTRACT_ABI,
      functionName: 'reveal',
      args: [roomId, roundCounter, move, salt],
    });
    let hash = await sendTransaction(
      {
        to: contract,
        data: data,
      },
      {
        //TODO: remove before production
        // sponsor: true, // ✅ Privy Paymaster covers gas fees
        uiOptions: {
          showWalletUIs: false,
        },
      },
    );
    return hash?.hash ?? null;
  }
  const { request } = await publicClient.simulateContract({
    address: contract,
    abi: RSP_CONTRACT_ABI,
    functionName: 'reveal',
    args: [roomId, roundCounter, move, salt],
    account: userAddress,
  });

  const estimatedGas = await publicClient.estimateContractGas({
    ...request,
    account: userAddress,
  });

  const gasWithBuffer = estimatedGas * 3n;

  return await walletClient.writeContract({
    ...request,
    gas: gasWithBuffer,
  });
}

export interface ICloseRoom {
  roomId: bigint;
  walletClient: WalletClient;
  publicClient: PublicClient;
  userAddress: `0x${string}`;
}

export async function closeRoom({ roomId, walletClient, publicClient, userAddress }: ICloseRoom) {
  const { request } = await publicClient.simulateContract({
    address: contract,
    abi: RSP_CONTRACT_ABI,
    functionName: 'closeRoom',
    args: [roomId],
    account: userAddress,
  });

  return await walletClient.writeContract({ ...request });
}

export interface IExitRoom {
  roomId: bigint;
  walletClient: WalletClient;
  publicClient: PublicClient;
  userAddress: `0x${string}`;
}

export async function exitRoom({ roomId, walletClient, publicClient, userAddress }: IExitRoom) {
  const { request } = await publicClient.simulateContract({
    address: contract,
    abi: RSP_CONTRACT_ABI,
    functionName: 'exitRoom',
    args: [roomId],
    account: userAddress,
  });

  return await walletClient.writeContract({ ...request });
}
